import type React from "react"

import type { AutocompleteOption } from "@/types/form-fields"

export const toStringOrEmpty = (value?: string | number | null): string =>
  value === null || value === undefined ? "" : String(value)

export const toStringOrNull = (value: string): string | null => {
  const trimmedValue = value.trim()

  if (!trimmedValue) return null

  return trimmedValue
}

export const toNumberOrNull = (value?: string | null): number | null => {
  if (value === null || value === undefined) return null

  const trimmedValue = value.trim()

  if (!trimmedValue) return null

  return isNumericString(trimmedValue) ? Number(trimmedValue) : null
}

export const isFinanceString = (value: string, decimals: number): boolean =>
  // eslint-disable-next-line security/detect-non-literal-regexp -- Using trusted constant UOM_ROUNDING_PRECISION
  new RegExp(`^\\d+(?:\\.\\d{1,${decimals}})?$`).test(value)

export const isNumericString = (value: string): boolean => {
  // eslint-disable-next-line security/detect-unsafe-regex
  const numericRegex = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/

  return numericRegex.test(value)
}

export const isEmptyValue = (value: any): boolean => {
  return value === null || value === undefined || value === ""
}

export const isFieldFilled = (value: any): boolean => {
  if (isEmptyValue(value)) return false

  if (typeof value === "object" && !Array.isArray(value)) {
    return Object.values(value).every(isFieldFilled)
  }

  return true
}

export const isLengthValid = (value: string | undefined | null, min: number, max: number): boolean => {
  if (!value) return false

  const length = value.trim().length

  return length >= min && length <= max
}

export const isValueInRange = (value: string, min: number, max: number): boolean => {
  const numericValue = Number(value)

  return !isNaN(numericValue) && numericValue >= min && numericValue <= max
}

export const getAutoCompleteOptionLabel = (option: AutocompleteOption): string => {
  return option.label
}

/**
 * Executes a callback while temporarily setting a guard ref to prevent cascading updates.
 *
 * This utility prevents onChange handlers from firing during programmatic form updates.
 * The delay ensures React completes its render cycle and all onChange callbacks have been
 * processed before clearing the guard flag.
 *
 * @template T - The type of the guard ref value (defaults to boolean)
 * @param callback - The function to execute while the guard is active
 * @param guardRef - The ref to use as a guard flag
 * @param options - Optional configuration
 * @param options.activeValue - Value to set when guard is active (defaults to true for boolean refs)
 * @param options.inactiveValue - Value to set when guard clears (defaults to false for boolean refs)
 * @param options.delay - Milliseconds to wait before clearing guard (defaults to 100ms)
 */
export const runWithGuardRef = <T = boolean>(
  callback: () => void,
  guardRef: React.MutableRefObject<T>,
  options?: {
    activeValue?: T
    inactiveValue?: T
    delay?: number
  }
): void => {
  const { activeValue = true as T, inactiveValue = false as T, delay = 100 } = options ?? {}

  guardRef.current = activeValue
  callback()
  setTimeout(() => {
    guardRef.current = inactiveValue
  }, delay)
}
